import { trackEvent } from '../../../../util/trackingHelper';
import { bytesToMB } from './helpers';

const ANALYTICS_INTERVAL_MS = 10000;

const STATUS = {
  IN_PROGRESS: 'IN_PROGRESS',
  CANCELLED: 'CANCELLED',
  ERROR: 'ERROR',
  COMPLETE: 'COMPLETE',
};

const ERROR_TYPES = {
  dimensions: 'IMAGE_TOO_SMALL',
  fileType: 'WRONG_FILE_TYPE',
  size: 'FILE_TOO_BIG',
  image: 'NOT_IMAGE',
  default: 'UNKNOWN',
  baddata: 'INVALID_INPUT',
  upload: 'NETWORK_ERROR',
  info: 'INFO_NOT_LOADED',
  createGroup: 'FILE_GROUP_CREATE_FAILED',
  deleted: 'FILE_DELETED',
};

// Returns a copy of a given object without all null and undefined properties
const withoutEmptyProps = original => {
  if (!original) return null;

  const cleaned = {};

  Object.keys(original).forEach(key => {
    const value = original[key];
    if (value === null || value === undefined) return;
    cleaned[key] = value;
  });

  return cleaned;
};

export const getUploadAnalytics = (product, location) => {
  let analyticsIntervalID;
  const allUploads = [];

  // Send an analytics event with status info about all uploads.
  const sendEvent = () => {
    const untrackedUploads = allUploads.filter(u => !u.fileTrackingComplete);
    const uploads = [];

    if (untrackedUploads.length === 0) return;

    untrackedUploads.forEach(fileInfo => {
      const { status, errorType, size, originalImageInfo, sourceInfo, duration } = fileInfo;

      // Mark COMPLETE / ERROR / CANCELLED uploads as tracked to avoid resending them later.
      // eslint-disable-next-line no-param-reassign
      fileInfo.fileTrackingComplete = true;

      uploads.push(
        withoutEmptyProps({
          status,
          error: errorType,
          product,
          location,
          size: size ? bytesToMB(size) : 0,
          w: originalImageInfo && originalImageInfo.width,
          h: originalImageInfo && originalImageInfo.height,
          source: sourceInfo && sourceInfo.source && sourceInfo.source.toUpperCase(),
          duration: Math.round(duration / 1000),
        })
      );
    });

    trackEvent('Upload Completed', { uploads });
  };

  // If there is a pending analytics event, immediately send it and cancel the interval
  const stopAnalytics = () => {
    if (analyticsIntervalID) {
      clearInterval(analyticsIntervalID);
      analyticsIntervalID = null;

      sendEvent();
    }
  };

  const startAnalytics = fileColl => {
    // eslint-disable-next-line no-underscore-dangle
    const widgetFiles = fileColl.__items;

    fileColl.onAdd.add(file => {
      const startTime = Date.now();

      // Whenever an upload starts, if the interval hasn't started, start it.
      if (!analyticsIntervalID) {
        analyticsIntervalID = setInterval(sendEvent, ANALYTICS_INTERVAL_MS);
      }

      file.fail((error, fileInfo) => {
        // 'user' errors mean that the user manually cancelled the upload. They shouldn't count as actual errors.
        allUploads.push({
          ...fileInfo,
          status: error === 'user' ? STATUS.CANCELLED : STATUS.ERROR,
          errorType: error === 'user' ? null : ERROR_TYPES[error],
          duration: Date.now() - startTime,
        });
      });

      file.done(fileInfo => {
        allUploads.push({
          ...fileInfo,
          status: STATUS.COMPLETE,
          duration: Date.now() - startTime,
        });
      });

      file.always(() => {
        // When the LAST file completes, immediately stop interval and send event.
        const allFilesFinished = !widgetFiles.some(f => f.state() === 'pending');
        if (allFilesFinished) {
          stopAnalytics();
        }
      });
    });
  };

  return { startAnalytics, stopAnalytics };
};
